import { useState } from "react";
import { Link } from "react-router-dom";
import HeroCarousel from "./components/HeroCarousel";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/base/Pagination";
import { useSeo } from "@/hooks/useSeo";
import {
  categories,
  formatViews,
  getFeaturedMovies,
  getLatestMovies,
  getPopularMovies,
  paginate,
} from "@/utils/movie";

export default function Home() {
  useSeo({
    title: "免费在线电影 - 高清电影在线观看平台",
    description: "免费在线电影为您提供最新高清电影资源，涵盖各类电影类型，支持在线观看和影片信息浏览，让您轻松享受丰富影视内容体验。",
    keywords: "免费在线电影,在线观看电影,高清电影,最新电影,电影资源",
  });

  const [page, setPage] = useState(1);
  const featured = getFeaturedMovies();
  const latest = getLatestMovies();
  const popular = getPopularMovies(5);
  const { items, totalPages, currentPage } = paginate(latest, page, 8);

  return (
    <div className="w-full">
      <HeroCarousel items={featured} />

      {/* Category quick entries */}
      <section className="mx-auto w-full px-4 py-12 md:px-6">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950">热门分类</h2>
            <p className="mt-1 text-sm text-foreground-600">按类型快速找到你想看的电影</p>
          </div>
          <Link to="/search" className="hidden items-center gap-1 text-sm font-medium text-accent-600 hover:text-accent-700 sm:flex">
            全部影片
            <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-all duration-300 hover:-translate-y-1 hover:border-accent-300 hover:bg-accent-50"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600 transition-colors group-hover:bg-accent-500 group-hover:text-background-50">
                <i className={`${cat.icon} text-2xl`}></i>
              </span>
              <div className="min-w-0">
                <p className="font-heading text-sm font-semibold text-foreground-950">{cat.name}</p>
                <p className="truncate text-xs text-foreground-500">精选{cat.name}影片</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Latest + Popular */}
      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto grid w-full grid-cols-1 gap-8 px-4 py-12 md:px-6 lg:grid-cols-[1fr_320px]">
          <div>
            <div className="mb-6 flex items-center gap-2">
              <i className="ri-film-line text-2xl text-accent-500"></i>
              <h2 className="font-heading text-2xl font-bold text-foreground-950">最新电影推荐</h2>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {items.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setPage} />
          </div>

          <aside>
            <div className="mb-6 flex items-center gap-2">
              <i className="ri-bar-chart-box-line text-2xl text-accent-500"></i>
              <h2 className="font-heading text-2xl font-bold text-foreground-950">热门电影排行</h2>
            </div>
            <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
              {popular.map((movie, i) => (
                <Link
                  key={movie.id}
                  to={`/detail/${movie.id}`}
                  className="group flex items-center gap-3 rounded-md p-1.5 transition-colors hover:bg-background-100"
                >
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-heading text-sm font-bold ${
                      i < 3 ? "bg-accent-500 text-background-50" : "bg-secondary-100 text-secondary-800"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <div className="h-14 w-10 shrink-0 overflow-hidden rounded-md bg-secondary-200">
                    <img src={movie.poster} alt={`${movie.title} 海报`} title={movie.title} className="h-full w-full object-top" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-foreground-900 transition-colors group-hover:text-accent-600">
                      {movie.title}
                    </p>
                    <p className="mt-0.5 flex items-center gap-2 text-xs text-foreground-500">
                      <span className="flex items-center gap-0.5">
                        <i className="ri-fire-line text-accent-500"></i>
                        {formatViews(movie.views)}
                      </span>
                      <span>·</span>
                      <span>{movie.year}</span>
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </section>

      {/* Site intro */}
      <section className="mx-auto w-full px-4 py-14 md:px-6">
        <div className="rounded-lg border border-background-200 bg-gradient-to-br from-primary-50 to-background-50 p-6 md:p-10">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">关于免费在线电影</h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
            免费在线电影是一个专注于高清影片信息浏览的影视内容平台，汇聚动作、剧情、喜剧、科幻、爱情、恐怖等十大主流类型，
            为影迷提供最新电影推荐、热门排行榜与便捷的分类筛选。我们致力于用简洁清晰的界面，让你更快找到想看的好片。
          </p>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-lg bg-background-50 p-5">
              <i className="ri-database-2-line text-2xl text-primary-500"></i>
              <p className="mt-2 font-heading text-xl font-bold text-foreground-950">5000+</p>
              <p className="text-sm text-foreground-600">海量影片资料</p>
            </div>
            <div className="rounded-lg bg-background-50 p-5">
              <i className="ri-grid-line text-2xl text-accent-500"></i>
              <p className="mt-2 font-heading text-xl font-bold text-foreground-950">10 大类</p>
              <p className="text-sm text-foreground-600">主流电影类型</p>
            </div>
            <div className="rounded-lg bg-background-50 p-5">
              <i className="ri-time-line text-2xl text-primary-500"></i>
              <p className="mt-2 font-heading text-xl font-bold text-foreground-950">每日更新</p>
              <p className="text-sm text-foreground-600">最新影片推荐</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}